/**
 * @fileoverview Single org-member picker for template items (MOR-696)
 *
 * Compact searchable picker used for a template item's assignee / assigned-by.
 * Stores a `users` id (or null). When the stored id is no longer an org member
 * (e.g. the person left the firm) it renders a greyed "Removed user" state so
 * the practitioner knows the slot will be dropped on apply — graceful failure.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Check, ChevronDown, UserPlus } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { UserAvatar, getDisplayName } from "@/ui/composed";
import { cn } from "@/utils/misc";

export function MemberSelect({
  orgId,
  value,
  onChange,
  placeholder = "Unassigned",
  clearOptionLabel,
}: {
  orgId: Id<"organizations">;
  value: Id<"users"> | null;
  onChange: (next: Id<"users"> | null) => void;
  placeholder?: string;
  /** Label for the "no user" option (defaults to the placeholder). */
  clearOptionLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const { data: members } = useQuery(
    convexQuery(api.organizations.queries.getOrganizationMembers, { orgId }),
  );

  const current = useMemo(
    () => (members ?? []).find((m) => m.user._id === value)?.user,
    [members, value],
  );

  // Stored id that no longer resolves to a member → graceful "removed" state.
  const missing = value !== null && members !== undefined && !current;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-2.5 text-sm",
            "transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          )}
        >
          {current ? (
            <UserAvatar
              avatarUrl={current.avatarUrl}
              firstName={current.firstName}
              lastName={current.lastName}
              email={current.email}
              className="size-5 shrink-0 text-[9px]"
            />
          ) : (
            <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed border-foreground/30 text-foreground/50">
              <UserPlus className="size-3" />
            </span>
          )}
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-left",
              missing
                ? "italic text-muted-foreground"
                : current
                  ? "text-foreground"
                  : "text-muted-foreground",
            )}
          >
            {missing
              ? "Removed user — will be skipped"
              : current
                ? getDisplayName(
                    current.firstName,
                    current.lastName,
                    current.email,
                  )
                : placeholder}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search members…" className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>No members found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="Unassigned"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="text-xs"
              >
                <Check
                  className={cn(
                    "mr-2 size-3.5",
                    !value ? "opacity-100" : "opacity-0",
                  )}
                />
                {clearOptionLabel ?? placeholder}
              </CommandItem>
              {(members ?? []).map((m) => {
                const name = getDisplayName(
                  m.user.firstName,
                  m.user.lastName,
                  m.user.email,
                );
                return (
                  <CommandItem
                    key={m.user._id}
                    value={name}
                    onSelect={() => {
                      onChange(m.user._id);
                      setOpen(false);
                    }}
                    className="gap-2 text-xs"
                  >
                    <Check
                      className={cn(
                        "size-3.5",
                        value === m.user._id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <UserAvatar
                      avatarUrl={m.user.avatarUrl}
                      firstName={m.user.firstName}
                      lastName={m.user.lastName}
                      email={m.user.email}
                      className="size-5 text-[9px]"
                    />
                    <span className="truncate">{name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
